import { CustomerError } from "../../ui/CustomerError";
import { GlassChip, BlobCtaButton, Typography } from "@cupcodev/ui";
import { EmptyState, NaveIcon, PageHeader, PageSkeleton, Panel, SectionSkeleton } from "../../ui/NavePage";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { CustomerDecisionDetail } from "../../read-models/collaboration/customer-decision";
import type { CustomerFeedbackThreadReadModel } from "../../read-models/collaboration/customer-feedback-thread";
import { useI18n } from "../i18n/I18nProvider";
import { useScenario } from "../scenario/ScenarioProvider";

type QueryError = Readonly<{ code: string }>;
type QueryResult<T> = Readonly<{ ok: true; value: T }> | Readonly<{ ok: false; error: QueryError }>;
type LoadState<T> = Readonly<{ kind: "loading" }> | Readonly<{ kind: "ready"; value: T }> | Readonly<{ kind: "error"; code: string }>;

const useCollaborationLoad = <T,>(loadKey: string, load: () => Promise<QueryResult<T>>): LoadState<T> => {
  const [state, setState] = useState<LoadState<T>>({ kind: "loading" });
  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    void load().then((result) => {
      if (cancelled) return;
      setState(result.ok ? { kind: "ready", value: result.value } : { kind: "error", code: result.error.code });
    });
    return () => { cancelled = true; };
  }, [loadKey]);
  return state;
};

const CollaborationLoading = ({ label }: { label: string }) => <PageSkeleton label={label} layout="detail" />;

const CollaborationError = ({ code, inline = false }: { code: string; inline?: boolean }) => {
  const { t } = useI18n();
  const key = code === "FORBIDDEN"
    ? "errors.forbidden"
    : code.startsWith("DEPENDENCY_")
      ? "errors.unavailable"
      : "errors.notFound";
  return <CustomerError inline={inline} title={t(`${key}.title`)} description={t(`${key}.body`)} />;
};

const feedbackAuthorLabel = (author: string, t: (key: string) => string): string =>
  t(`feedback.author.${author}`);

export const ApprovalFeedbackPanel = ({ subjectRef }: { subjectRef: string }) => {
  const { runtime, activeOrganizationId } = useScenario();
  const { t, formatDate } = useI18n();
  const [reload, setReload] = useState(0);
  const [body, setBody] = useState("");
  const [mutation, setMutation] = useState<Readonly<{ kind: "idle" | "submitting" | "success" | "error"; code?: string }>>({ kind: "idle" });
  const retryToken = useRef<Readonly<{ fingerprint: string; idempotencyKey: string }> | undefined>(undefined);
  const state = useCollaborationLoad<CustomerFeedbackThreadReadModel>(
    `${runtime.scenario.id}:${activeOrganizationId.value}:feedback:${subjectRef}:${reload}`,
    () => runtime.experience.getFeedbackThread(subjectRef),
  );

  const submit = (detail: CustomerFeedbackThreadReadModel): void => {
    if (!detail.availableActions.includes("COMMENT") || mutation.kind === "submitting") return;
    const normalized = body.trim();
    if (normalized.length === 0) {
      setMutation({ kind: "error", code: "VALIDATION_ERROR" });
      return;
    }
    const fingerprint = `${detail.threadId}:${detail.versionId}:${detail.responseRevision}:${normalized}`;
    let token = retryToken.current;
    if (token === undefined || token.fingerprint !== fingerprint) {
      const nonce = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      token = { fingerprint, idempotencyKey: `feedback-${detail.threadId}-${nonce}` };
      retryToken.current = token;
    }
    setMutation({ kind: "submitting" });
    void runtime.experience.submitFeedback({
      subjectRef,
      ...(detail.versionId===undefined?{}:{versionId:detail.versionId}),
      ...(detail.responseRevision===undefined?{}:{expectedRevision:detail.responseRevision}),
      body: normalized,
      idempotencyKey: token.idempotencyKey,
    }).then((result) => {
      if (!result.ok) {
        setMutation({ kind: "error", code: result.error.code });
        if (result.error.code !== "DEPENDENCY_UNAVAILABLE") retryToken.current = undefined;
        return;
      }
      retryToken.current = undefined;
      setBody("");
      setMutation({ kind: "success" });
      setReload((value) => value + 1);
    });
  };

  if (state.kind === "loading") {
    return <Panel icon="lucide:messages-square" title={t("feedback.title")}><SectionSkeleton label={t("state.loading")} rows={2} /></Panel>;
  }
  if (state.kind === "error") {
    if (state.code === "NOT_FOUND" || state.code === "FORBIDDEN") return null;
    return <Panel icon="lucide:messages-square" title={t("feedback.title")}><EmptyState icon="lucide:cloud-off">{t("feedback.unavailable")}</EmptyState></Panel>;
  }
  const detail = state.value;
  const busy = mutation.kind === "submitting";
  return <Panel icon="lucide:messages-square" title={t("feedback.title")} className="feedback-panel" {...(detail.comments.length ? { count: detail.comments.length } : {})} action={<GlassChip variant="neutral">{t(`feedback.status.${detail.state}`)}</GlassChip>}>
    <Typography as="p" variant="body-sm" tone="muted">{t("feedback.subtitle")}</Typography>
    {detail.comments.length === 0 ? <EmptyState icon="lucide:message-circle">{t("feedback.empty")}</EmptyState> : <ol className="nave-thread">{detail.comments.map((comment) => <li key={comment.commentId} className={comment.author !== "CUPCODE" ? "nave-message nave-message-client" : "nave-message"}><span className="nave-message-head"><strong>{feedbackAuthorLabel(comment.author, t)}</strong><time dateTime={comment.createdAt}>{formatDate(comment.createdAt)}</time></span><p className="nave-message-bubble">{comment.body}</p></li>)}</ol>}
    {detail.availableActions.includes("COMMENT") ? <form className="nave-form" onSubmit={(event: { preventDefault(): void }) => { event.preventDefault(); submit(detail); }}>
      <label className="nave-field"><span>{t("feedback.commentLabel")}</span><textarea value={body} onChange={(event: { target: { value: string } }) => setBody(event.target.value)} rows={3} disabled={busy} aria-describedby="feedback-action-status" /></label>
      <div className="nave-form-actions">
        <BlobCtaButton type="submit" disabled={busy || body.trim().length === 0} loading={busy}>{t("feedback.submit")}</BlobCtaButton>
        <span id="feedback-action-status" aria-live="polite" className="nave-status-line">{mutation.kind === "submitting" ? t("feedback.submitting") : mutation.kind === "success" ? t("feedback.success") : mutation.kind === "error" ? t(mutation.code === "OWNER_MUTATION_UNAVAILABLE" ? "feedback.ownerUnavailable" : mutation.code === "CONFLICT" ? "feedback.closed" : mutation.code === "VALIDATION_ERROR" ? "feedback.validation" : "feedback.error") : ""}</span>
      </div>
    </form> : <EmptyState icon="lucide:lock">{t(detail.ownership === "EXTERNAL_OWNED" ? "feedback.externalReadOnly" : "feedback.readOnly")}</EmptyState>}
  </Panel>;
};

const decisionLabel = (item: Readonly<{ labelKey?: string | undefined; labelText?: string | undefined }>, fallback: string, t: (key: string) => string): string =>
  item.labelText ?? (item.labelKey === undefined ? fallback : t(item.labelKey));

const decisionStatus = (state: string, t: (key: string) => string): string => t(`decisions.status.${state}`);

export const DecisionDetailPage = () => {
  const { decisionId = "" } = useParams();
  const { runtime, activeOrganizationId } = useScenario();
  const { t, formatDate } = useI18n();
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [mutation, setMutation] = useState<Readonly<{ kind: "idle" | "submitting" | "success" | "error"; code?: string }>>({ kind: "idle" });
  const retryToken = useRef<Readonly<{ fingerprint: string; idempotencyKey: string; correlationId: string }> | undefined>(undefined);
  const state = useCollaborationLoad<CustomerDecisionDetail>(
    `${runtime.scenario.id}:${activeOrganizationId.value}:decision:${decisionId}:${reload}`,
    () => runtime.experience.getDecision(decisionId),
  );

  useEffect(() => {
    if (state.kind === "ready") setSelected(state.value.selection?.selectedOptionIds ?? []);
  }, [state.kind === "ready" ? state.value.updatedAt : state.kind]);

  const toggle = (optionId: string, mode: CustomerDecisionDetail["selectionMode"]): void => {
    if (mode === "SINGLE") {
      setSelected([optionId]);
      return;
    }
    setSelected((current) => current.includes(optionId) ? current.filter((item) => item !== optionId) : [...current, optionId]);
  };

  const submit = (detail: CustomerDecisionDetail): void => {
    if (!detail.availableActions.includes("SELECT") || mutation.kind === "submitting") return;
    const normalizedIds = [...selected].sort();
    const fingerprint = `${detail.decisionId}:${detail.responseRevision}:${normalizedIds.join(",")}`;
    let token = retryToken.current;
    if (token === undefined || token.fingerprint !== fingerprint) {
      const nonce = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      token = {
        fingerprint,
        idempotencyKey: `decision-${detail.decisionId}-${nonce}`,
        correlationId: `decision-ui-${nonce}`,
      };
      retryToken.current = token;
    }
    setMutation({ kind: "submitting" });
    void runtime.experience.respondDecision({
      decisionId: detail.decisionId,
      ...(detail.versionId===undefined?{}:{versionId:detail.versionId}),
      expectedRevision: detail.responseRevision,
      selectedOptionIds: normalizedIds,
      idempotencyKey: token.idempotencyKey,
      correlationId: token.correlationId,
      clientApplicationId: "nave-web",
    }).then((result) => {
      if (!result.ok) {
        setMutation({ kind: "error", code: result.error.code });
        if (result.error.code !== "DEPENDENCY_UNAVAILABLE") retryToken.current = undefined;
        if (result.error.code === "CONFLICT") setReload((value) => value + 1);
        return;
      }
      retryToken.current = undefined;
      setMutation({ kind: "success" });
      setReload((value) => value + 1);
    });
  };

  if (state.kind === "loading") return <CollaborationLoading label={t("state.loading")} />;
  if (state.kind === "error") return <CollaborationError code={state.code} />;
  const detail = state.value;
  const prompt = detail.promptText ?? (detail.promptKey === undefined ? t("decisions.untitled") : t(detail.promptKey));
  const context = detail.contextText ?? (detail.contextKey === undefined ? t("decisions.contextDefault") : t(detail.contextKey));
  const busy = mutation.kind === "submitting";
  return <div className="nave-page">
    <PageHeader
      crumbs={[{ label: t("decisions.listTitle"), to: "/decisions" }, { label: prompt }]}
      icon="lucide:split"
      title={prompt}
      description={context}
      status={{ label: decisionStatus(detail.state, t), tone: detail.state === "OPEN" ? "purple" : "neutral" }}
    />
    <div className="nave-layout">
      <div className="nave-layout-main">
        <Panel icon="lucide:list-checks" title={t("decisions.optionsLabel")}>
          {detail.availableActions.includes("SELECT") ? <form className="nave-form" onSubmit={(event: { preventDefault(): void }) => { event.preventDefault(); submit(detail); }}>
            <fieldset className="nave-choices"><legend className="sr-only">{t("decisions.optionsLabel")}</legend>{detail.options.map((option) => <label key={option.optionId} className="nave-choice"><input type={detail.selectionMode === "SINGLE" ? "radio" : "checkbox"} name="decision-option" value={option.optionId} checked={selected.includes(option.optionId)} onChange={() => toggle(option.optionId, detail.selectionMode)} disabled={busy} /><span><Typography as="strong" variant="body-md">{decisionLabel(option, t("decisions.optionUntitled"), t)}</Typography>{option.descriptionKey !== undefined || option.descriptionText !== undefined ? <Typography as="span" variant="body-sm" tone="muted">{decisionLabel({ labelKey: option.descriptionKey, labelText: option.descriptionText }, "", t)}</Typography> : null}</span></label>)}</fieldset>
            <div className="nave-form-actions"><BlobCtaButton type="submit" disabled={busy || selected.length === 0} loading={busy}>{t("decisions.submit")}</BlobCtaButton><span aria-live="polite" className="nave-status-line">{mutation.kind === "submitting" ? t("decisions.submitting") : mutation.kind === "success" ? t("decisions.success") : mutation.kind === "error" ? t(mutation.code === "CONFLICT" ? "decisions.stale" : mutation.code === "OWNER_MUTATION_UNAVAILABLE" ? "decisions.ownerUnavailable" : mutation.code === "VALIDATION_ERROR" ? "decisions.validation" : "decisions.error") : ""}</span></div>
          </form> : <div className="nave-choices">{detail.options.map((option) => { const chosen = detail.selection?.selectedOptionIds.includes(option.optionId) === true; return <div key={option.optionId} className="nave-choice nave-choice-readonly" style={chosen ? { borderColor: "var(--cupcode-pink)" } : undefined}><NaveIcon name={chosen ? "lucide:circle-check" : "lucide:circle"} /><span><Typography as="strong" variant="body-md">{decisionLabel(option, t("decisions.optionUntitled"), t)}</Typography>{option.descriptionKey !== undefined || option.descriptionText !== undefined ? <Typography as="span" variant="body-sm" tone="muted">{decisionLabel({ labelKey: option.descriptionKey, labelText: option.descriptionText }, "", t)}</Typography> : null}</span></div>; })}</div>}
          {detail.availableActions.length === 0 && detail.state === "OPEN" ? <EmptyState icon="lucide:lock">{t(detail.ownership === "EXTERNAL_OWNED" ? "decisions.externalReadOnly" : "decisions.readOnly")}</EmptyState> : null}
        </Panel>
      </div>
      <aside className="nave-layout-aside" aria-label={t("layout.context")}>
        {detail.selection !== undefined ? <Panel icon="lucide:circle-check" title={t("decisions.selection")}><span className="nave-meta-item"><NaveIcon name="lucide:calendar" />{formatDate(detail.selection.selectedAt)}</span>{detail.selection.comment ? <p className="nave-prose">{detail.selection.comment}</p> : null}</Panel> : null}
        {detail.history.length > 0 ? <Panel icon="lucide:history" title={t("decisions.history")} tone="glass-sm"><ol className="nave-timeline">{detail.history.map((item, index) => <li key={`${item.type}:${item.occurredAt}:${index}`}><span className="nave-timeline-dot" aria-hidden="true"><NaveIcon name="lucide:dot" /></span><div className="nave-timeline-body"><p className="nave-timeline-title">{t(`decisions.history.${item.type}`)}</p><time className="nave-timeline-time" dateTime={item.occurredAt}>{formatDate(item.occurredAt)}</time></div></li>)}</ol></Panel> : null}
      </aside>
    </div>
  </div>;
};
